import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/components/ui/Button/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/DropdownMenu/DropdownMenu';

import { KEY_VALUE_EMPTY_CLASSES } from '../structured.tokens';

import { type KeyValueAddControlProps } from './KeyValueAddControl.types';

/**
 * How a row is added — and, with a catalogue, how a key stops being typed.
 *
 * Three shapes, decided entirely by what the caller knows:
 *
 *   no catalogue      → one button; the key is typed into the row.
 *   open catalogue    → a menu of the keys not on screen, plus "Custom key…".
 *   closed catalogue  → the same menu, without the custom escape. When it runs
 *                       out, the control DISAPPEARS and says so — for an
 *                       exhaustive schema (`z.record(z.enum(…), …)`) every key
 *                       is already there and there is nothing left to add.
 *
 * The menu is a `DropdownMenu` rather than a combobox on purpose: Radix gives
 * typeahead for free, and a combobox would still PERMIT a key the schema
 * refuses — which is the state this control exists to remove, not to warn about.
 *
 * Presentation only (U5).
 */
export function KeyValueAddControl({
  remaining,
  hasCatalogue,
  canAddCustom,
  isDisabled,
  onAdd,
  registerControl,
}: KeyValueAddControlProps) {
  const { t } = useTranslation('uiKit');

  if (!hasCatalogue) {
    return (
      <Button
        ref={registerControl}
        type="button"
        variant="outline"
        size="sm"
        disabled={isDisabled}
        startIcon={<Plus aria-hidden className="h-4 w-4" />}
        onClick={() => {
          onAdd();
        }}
      >
        {t('keyValue.addRow')}
      </Button>
    );
  }

  if (remaining.length === 0 && !canAddCustom) {
    return <p className={KEY_VALUE_EMPTY_CLASSES}>{t('keyValue.allKeysAdded')}</p>;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          ref={registerControl}
          type="button"
          variant="outline"
          size="sm"
          disabled={isDisabled}
          startIcon={<Plus aria-hidden className="h-4 w-4" />}
        >
          {t('keyValue.addEntry')}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {remaining.length === 0 ? null : (
          <>
            <DropdownMenuLabel>{t('keyValue.addMenuLabel')}</DropdownMenuLabel>
            {remaining.map((entry) => (
              <DropdownMenuItem
                key={entry.key}
                onSelect={() => {
                  onAdd(entry.key);
                }}
              >
                {entry.label}
              </DropdownMenuItem>
            ))}
          </>
        )}
        {canAddCustom ? (
          <>
            {remaining.length === 0 ? null : <DropdownMenuSeparator />}
            <DropdownMenuItem
              onSelect={() => {
                onAdd();
              }}
            >
              {t('keyValue.addCustom')}
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
